/**
 * OMS orders list — status label/format helpers for Order Status & Allocation Status columns.
 */

import {formatSyncStatusLabel, normalizeSyncStatus} from './syncStatusDisplayUtils'

export type AllocationStatusValue = 'ALLOCATED' | 'UNALLOCATED' | 'PARTIALLY_ALLOCATED'

export type FulfillmentStatusValue =
  | 'PENDING'
  | 'SHIPPED'
  | 'PARTIALLY_SHIPPED'
  | 'ERROR'
  | 'DELIVERED'
  | 'PROCESSING'
  | 'NA'

export const normalizeStatusToken = (value: string | null | undefined): string =>
  (value || '')
    .trim()
    .toUpperCase()
    .replace(/-/g, '_')
    .replace(/\s+/g, '_')

/** Match platformorder_set entry by connected platform id (handles number/string/object platform). */
export const findPlatformOrderEntry = (
  row: any,
  platformId: string | number | null | undefined
) => {
  if (!row?.platformorder_set?.length || platformId == null || platformId === '') {
    return null
  }

  const targetId = Number(platformId)
  if (!Number.isFinite(targetId)) {
    return null
  }

  return (
    row.platformorder_set.find((item: any) => {
      const entryPlatform = item?.platform
      const entryId =
        typeof entryPlatform === 'object' && entryPlatform != null
          ? entryPlatform?.id
          : entryPlatform
      return Number(entryId) === targetId
    }) ?? null
  )
}

export const normalizeAllocationStatus = (
  status: string | null | undefined
): AllocationStatusValue => {
  if (!status?.trim()) return 'UNALLOCATED'

  const normalized = normalizeStatusToken(status)
  if (['FULLY_ALLOCATED', 'FULLYALLOCATED', 'ALLOCATED'].includes(normalized)) {
    return 'ALLOCATED'
  }
  if (['PARTIALLY_ALLOCATED', 'PARTIAL', 'PARTIALLY'].includes(normalized)) {
    return 'PARTIALLY_ALLOCATED'
  }
  if (['UNALLOCATED', 'NOT_ALLOCATED'].includes(normalized)) {
    return 'UNALLOCATED'
  }

  return 'UNALLOCATED'
}

export const formatAllocationStatusLabel = (
  status: string | null | undefined
): string => {
  switch (normalizeAllocationStatus(status)) {
    case 'ALLOCATED':
      return 'Allocated'
    case 'PARTIALLY_ALLOCATED':
      return 'Partially Allocated'
    case 'UNALLOCATED':
    default:
      return 'Unallocated'
  }
}

/** Map allocation_status to StatusButton syncStatus for badge colors. */
export const mapAllocationStatusToSyncStatus = (
  status: string | null | undefined
): string => {
  switch (normalizeAllocationStatus(status)) {
    case 'ALLOCATED':
      return 'SYNCED'
    case 'PARTIALLY_ALLOCATED':
      return 'SYNCED_PARTIALLY'
    case 'UNALLOCATED':
    default:
      return 'ERROR'
  }
}

export const normalizeFulfillmentStatus = (
  status: string | null | undefined
): FulfillmentStatusValue => {
  if (!status?.trim()) return 'NA'

  const normalized = normalizeStatusToken(status)
  if (normalized === 'PENDING') return 'PENDING'
  if (normalized === 'SHIPPED') return 'SHIPPED'
  if (['PARTIALLY_SHIPPED', 'PARTIAL_SHIPPED', 'PARTIALLY'].includes(normalized)) {
    return 'PARTIALLY_SHIPPED'
  }
  if (['ERROR', 'FAILED', 'FAIL'].includes(normalized)) return 'ERROR'
  if (normalized === 'DELIVERED') return 'DELIVERED'
  if (normalized === 'PROCESSING') return 'PROCESSING'
  return normalized as FulfillmentStatusValue
}

export const formatFulfillmentStatusLabel = (
  status: string | null | undefined
): string => {
  const normalized = normalizeFulfillmentStatus(status)
  if (normalized === 'NA') return 'NA'
  return formatSyncStatusLabel(normalized)
}

/** Map shipment_status to StatusButton syncStatus for badge colors. */
export const mapFulfillmentStatusToSyncStatus = (
  status: string | null | undefined
): string => {
  switch (normalizeFulfillmentStatus(status)) {
    case 'PENDING':
      return 'PENDING'
    case 'SHIPPED':
      return 'SYNCED'
    case 'PARTIALLY_SHIPPED':
      return 'SYNCED_PARTIALLY'
    case 'ERROR':
      return 'ERROR'
    case 'DELIVERED':
      return 'SUCCESS'
    case 'PROCESSING':
      return 'PROCESSING'
    case 'NA':
      return 'IGNORED'
    default:
      return normalizeSyncStatus(status) || 'IGNORED'
  }
}

export const formatOrderApiStatusLabel = (apiStatus: string | null | undefined): string => {
  if (!apiStatus?.trim()) return 'N/A'

  const normalized = normalizeStatusToken(apiStatus)
  const labelMap: Record<string, string> = {
    CANCELLED: 'Cancelled',
    CANCELED: 'Cancelled',
    COMPLETED: 'Completed',
    DRAFT: 'Draft',
    ON_HOLD: 'On-hold',
    ONHOLD: 'On-hold',
    PENDING: 'Pending',
    PENDING_APPROVAL: 'Pending-Approval',
    OPEN: 'Open',
  }

  return labelMap[normalized] ?? apiStatus.trim()
}

/** Map api_status to StatusButton syncStatus for badge colors. */
export const mapOrderApiStatusToSyncStatus = (apiStatus: string | null | undefined): string => {
  if (!apiStatus?.trim()) return 'OTHER'

  const normalized = normalizeStatusToken(apiStatus)
  switch (normalized) {
    case 'CANCELLED':
    case 'CANCELED':
      return 'DELETE'
    case 'COMPLETED':
      return 'SYNCED'
    case 'DRAFT':
      return 'ONHOLD'
    case 'ON_HOLD':
    case 'ONHOLD':
      return 'ONHOLD'
    case 'PENDING':
      return 'PENDING'
    case 'OPEN':
      return 'OPEN'
    case 'PENDING_APPROVAL':
      return 'PENDING_APPROVAL'
    default:
      return normalized
  }
}
